import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { SystemAirflowResponse } from '../types';

/** FS-593 — after a save, how often the system is re-read and for how long. */
export const SAVE_POLL_INTERVAL_MS = 3000;
export const SAVE_POLL_MAX_MS = 30000;

/**
 * FS-593 — where the re-read after a save stands.
 * - `idle`: nothing is being waited for;
 * - `updating`: a save was made and the projection has not caught up yet;
 * - `gave_up`: 30 s went by without it catching up, so the caller offers a
 *   manual refresh instead of spinning.
 */
export type SystemAirflowRefresh = 'idle' | 'updating' | 'gave_up';

/**
 * True when a read shows the projection synced after the save. The baseline
 * is the `projection_updated_at` the page held BEFORE the write was sent (null
 * when it had none), so both sides are the server's clock and the browser's
 * never enters into it. The stamp must be present and differ from the
 * baseline; when both parse as times it must also be later.
 */
export const projectionAdvanced = (
  stamp: string | null | undefined,
  baseline: string | null,
): boolean => {
  if (!stamp) return false;
  if (baseline === null) return true;
  if (stamp === baseline) return false;
  const at = Date.parse(stamp);
  const before = Date.parse(baseline);
  if (Number.isFinite(at) && Number.isFinite(before)) return at > before;
  return true;
};

export interface UseSystemAirflowOptions {
  /**
   * Called once when a save poll sees the projection advance, so the rest of
   * the page (the device detail) can be read again to match it.
   */
  onSynced?: () => void;
}

/**
 * Plan 33 Phase 6 (first pass) — GET /devices/:id/system-airflow.
 * Loads the system airflow summary for the Overview tab and the Analysis page.
 *
 * FS-566: `refetch` reloads it. Every request takes a sequence number and only
 * the newest may write state, so a slow earlier response — the previous
 * device's, or the read from before the edit — can never overwrite a newer one.
 *
 * FS-593: `error` says the server could not be read, which the filter
 * configuration block tells apart from "nothing synced yet".
 * `projectionStamp()` is the `projection_updated_at` on screen now; a writer
 * takes it before sending, and hands it to `refreshAfterSave(baseline)`
 * once the write lands. The hook then re-reads every 3 s — never starting a
 * read while the previous one is still out. The first read whose stamp is
 * past the baseline clears `updating` and calls `onSynced`; reading goes on
 * quietly until 30 s, and every later advance is applied and calls
 * `onSynced` again, because the on-screen baseline can predate a resync
 * that landed before the write. A separate 30 s timer ends it, whatever the
 * requests are doing: silently after an advance, in `gave_up` without one. A second save,
 * a device change or unmounting ends the poll in progress. The window
 * regaining focus re-reads too, because the homeowner may have edited the
 * slots in the app. Background re-reads keep the figure on screen instead of
 * dropping back to the loading state, and a failed one leaves the last good
 * answer standing.
 */
export function useSystemAirflow(deviceId: number | undefined, options: UseSystemAirflowOptions = {}) {
  const [data, setData] = useState<SystemAirflowResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState<SystemAirflowRefresh>('idle');
  /** The save being followed; a new object for every save, so a second one restarts. */
  const [poll, setPoll] = useState<{ baseline: string | null } | null>(null);
  const latestRequest = useRef(0);
  const dataRef = useRef<SystemAirflowResponse | null>(null);
  dataRef.current = data;
  const onSyncedRef = useRef(options.onSynced);
  onSyncedRef.current = options.onSynced;

  /**
   * One read. Resolves with the response when it is still the newest request,
   * and with null when it failed or was overtaken.
   */
  const fetchOnce = useCallback(
    (quiet: boolean): Promise<SystemAirflowResponse | null> => {
      const request = ++latestRequest.current;
      if (!deviceId) {
        setData(null);
        setError(false);
        setLoading(false);
        return Promise.resolve(null);
      }
      if (!quiet) setLoading(true);
      return axiosInstance
        .get<SystemAirflowResponse>(
          `${config.devicesApiUrl}/devices/${encodeURIComponent(deviceId)}/system-airflow`,
        )
        .then((res) => {
          if (request !== latestRequest.current) return null;
          setData(res.data);
          setError(false);
          return res.data;
        })
        .catch((err) => {
          console.error('Failed to load system airflow:', err);
          if (request !== latestRequest.current) return null;
          if (!quiet) {
            setData(null);
            setError(true);
          }
          return null;
        })
        .finally(() => {
          if (request === latestRequest.current) setLoading(false);
        });
    },
    [deviceId],
  );

  const load = useCallback(() => {
    void fetchOnce(false);
  }, [fetchOnce]);

  useEffect(() => {
    // A different device: its predecessor's figure must not stay on screen
    // while the new one loads, and its poll is not this device's.
    setData(null);
    setError(false);
    setPoll(null);
    setRefresh('idle');
    load();
    return () => {
      // Unmount or device change: whatever is in flight is no longer newest.
      latestRequest.current += 1;
    };
  }, [load]);

  // FS-593: an edit made in the app reaches the console only by a re-read.
  useEffect(() => {
    if (!deviceId) return undefined;
    const onFocus = () => {
      void fetchOnce(true);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [deviceId, fetchOnce]);

  useEffect(() => {
    if (poll === null) return undefined;
    // The newest stamp seen since the save. The baseline is the stamp that was
    // ON SCREEN, which can be older than the projection at the moment of the
    // write (a resync nobody had read yet), so the first stamp past it may
    // still be pre-save. Every later advance is therefore taken too, until
    // the deadline.
    let lastSeen = poll.baseline;
    let advanced = false;
    let stopped = false;
    let inFlight = false;
    const stop = () => {
      stopped = true;
      window.clearInterval(tick);
      window.clearTimeout(deadline);
    };
    const tick = window.setInterval(() => {
      // A slow read is never overlapped: this tick is skipped instead.
      if (stopped || inFlight) return;
      inFlight = true;
      void fetchOnce(true).then((fresh) => {
        inFlight = false;
        if (stopped) return;
        if (fresh && projectionAdvanced(fresh.projection_updated_at, lastSeen)) {
          lastSeen = fresh.projection_updated_at ?? lastSeen;
          if (!advanced) {
            advanced = true;
            setRefresh('idle');
          }
          onSyncedRef.current?.();
        }
      });
    }, SAVE_POLL_INTERVAL_MS);
    // Independent of the reads: a request that never answers still ends here.
    // Silently when the stamp moved at least once (the page is already
    // showing a post-baseline read); with the manual refresh when it never did.
    const deadline = window.setTimeout(() => {
      if (stopped) return;
      stop();
      setPoll(null);
      if (!advanced) setRefresh('gave_up');
    }, SAVE_POLL_MAX_MS);
    return stop;
  }, [poll, fetchOnce]);

  /** The `projection_updated_at` on screen now: a writer's baseline, taken before it sends. */
  const projectionStamp = useCallback(
    (): string | null => dataRef.current?.projection_updated_at ?? null,
    [],
  );

  /** Follow a save whose write was sent while `baseline` was on screen. A second save restarts it. */
  const refreshAfterSave = useCallback((baseline: string | null) => {
    setRefresh('updating');
    setPoll({ baseline });
  }, []);

  /** The manual control: one re-read now, and the gave-up notice cleared. */
  const refetch = useCallback(() => {
    setPoll(null);
    setRefresh('idle');
    load();
  }, [load]);

  return { data, loading, error, refresh, refetch, refreshAfterSave, projectionStamp };
}
